import { Modal as AntModal, ModalProps } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import { ButtonVariant, Button, Icon, Typography, TypographyVariantsType } from '..';

import './Modal.scss';

export enum ModalSize {
  lg = 1170,
  md = 770,
}

type ModalSizeType = keyof typeof ModalSize;

interface Props extends ModalProps {
  size?: ModalSizeType;
  titleTypographyVariant?: TypographyVariantsType;
  styles?: ModalProps['styles'];
}

export const Modal: React.FC<Props> = ({
  children,
  title,
  className,
  width,
  size = 'md',
  closable = true,
  open,
  footer = null,
  centered = true,
  titleTypographyVariant = 'H2',
  onCancel,
  styles,
  ...props
}) => {
  useEffect(() => {
    open && (document.body.style.overflow = 'hidden');

    return (): void => {
      document.body.style.overflow = 'auto';
    };
  }, [open]);

  const computedStyles = useMemo(() => {
    const baseStyles = styles || {};
    const baseMaskStyle = baseStyles.mask || {};

    const maskStyleComputed: React.CSSProperties = baseMaskStyle;

    return {
      ...baseStyles,
      mask: maskStyleComputed,
    };
  }, [styles]);

  return (
    <AntModal
      {...props}
      className={classNames('custom-modal', `custom-modal--${size}`, className)}
      title={
        <div className="custom-modal__header">
          <Typography variant={titleTypographyVariant}>{title}</Typography>
          {closable && (
            <Button
              data-testid="modal-close-button"
              className="custom-modal__close-btn"
              variant={ButtonVariant.ICON_GHOST}
              onClick={onCancel}
            >
              <Icon name="close" />
            </Button>
          )}
        </div>
      }
      open={open}
      closable={false}
      onCancel={onCancel}
      width={width ? width : ModalSize[size]}
      footer={footer}
      centered={centered}
      keyboard
      styles={computedStyles}
    >
      {children}
    </AntModal>
  );
};
